import axiosInstance from './axios';

const BASE = '/admin/bubble-tea-options';

// ─── Types ────────────────────────────────────────────────────────────────────

// Widened from a fixed 5-value union to a plain string (task-028 Sprint 4),
// mirroring the backend's task-028-s2-2 schema widening: BubbleTeaOptionCategory.option_type
// is now a free-text String column (no CHECK constraint), so admins can create
// novel option_type values beyond the original tea_type/topping/sugar/ice/size set.
export type BubbleTeaOptionType = string;

export interface BubbleTeaOptionItem {
  id: number;
  category_id: number;
  name: string;
  // Simplified-Chinese translation of `name` (task-025 story-004-4). Nullable/optional --
  // mirrors backend BubbleTeaOptionItemResponse.name_zh in admin_bubbletea_options.py.
  name_zh?: string | null;
  extra_price: number;
  is_default: boolean;
  active: boolean;
  sort_order: number;
  // Short machine-QR code (e.g. 'S01'), globally unique across ALL option items
  // (case-insensitive). Server-generated when omitted on create; optional on
  // update (omitted => unchanged). Mirrors backend BubbleTeaOptionItemResponse.option_code.
  option_code?: string | null;
  // task-171: when true, selecting this item means no drink-machine combo QR is
  // available for the combo. Mirrors backend BubbleTeaOptionItemResponse.combo_qr_unavailable.
  combo_qr_unavailable: boolean;
  // Non-persisted, request-scoped signal (task-032 fix_review MEDIUM
  // finding): set on the response returned by the update/create call that
  // just renamed this item, or created a new item reusing a prior sibling's
  // name, in a way that may have orphaned or silently reused a
  // previously-minted drink-machine combo QR token. Absent/null on every
  // other response (e.g. the GET /categories list). Mirrors backend
  // BubbleTeaOptionItemResponse.combo_qr_warning in admin_bubbletea_options.py.
  combo_qr_warning?: string | null;
}

export interface BubbleTeaOptionCategory {
  id: number;
  name: string;
  // Simplified-Chinese translation of `name` (task-025 story-004-4). Nullable/optional --
  // mirrors backend BubbleTeaOptionCategoryResponse.name_zh in admin_bubbletea_options.py.
  name_zh?: string | null;
  option_type: BubbleTeaOptionType;
  // Whether a customer may pick one item ('single') or several ('multiple')
  // from this category. Mirrors backend BubbleTeaOptionCategoryResponse.selection_mode
  // (SelectionMode enum) in admin_bubbletea_options.py (task-028 Sprint 4).
  selection_mode: 'single' | 'multiple';
  // Whether this category's active/available option items should eagerly
  // generate combo-QR codes for affected order items (task-032). Mirrors
  // backend BubbleTeaOptionCategoryResponse.triggers_qr_variant in
  // admin_bubbletea_options.py. The backend rejects (422) triggers_qr_variant
  // =true combined with an effective selection_mode of 'multiple' -- the
  // admin UI mirrors this by disabling the checkbox in that case (see
  // AdminBubbleTeaOptions.tsx).
  triggers_qr_variant: boolean;
  sort_order: number;
  active: boolean;
  items: BubbleTeaOptionItem[];
  // Non-persisted, request-scoped signal (task-032 fix_review MEDIUM
  // finding): set on the response returned by the update call that just
  // renamed this category in a way that may have orphaned previously-minted
  // drink-machine combo QR tokens generated under the old name. Absent/null
  // on every other response (e.g. the GET /categories list). Mirrors
  // backend BubbleTeaOptionCategoryResponse.combo_qr_warning in
  // admin_bubbletea_options.py.
  combo_qr_warning?: string | null;
}

export interface InstantCakeItem {
  id: number;
  name: string;
  description: string | null;
  // Simplified-Chinese translations (task-025 story-004-6). Nullable/optional --
  // mirror backend InstantCakeItemResponse.name_zh/description_zh in
  // admin_bubbletea_options.py.
  name_zh?: string | null;
  description_zh?: string | null;
  price: number;
  stock_count: number;
  image_url: string | null;
  active: boolean;
  category: string | null;
  qr_enabled: boolean;
  qr_token?: string | null;
}

export interface BubbleTeaMenuItemAdmin {
  menu_item_id: number;
  name: string;
  description: string | null;
  // Simplified-Chinese translations (task-025 story-004-5). Nullable/optional --
  // mirror backend BubbleTeaMenuItemResponse.name_zh/description_zh in
  // admin_bubbletea_options.py.
  name_zh?: string | null;
  description_zh?: string | null;
  price: number;
  image_url: string | null;
  available: boolean;
  menu_category: string | null;
  qr_enabled: boolean;
  qr_token?: string | null;
  // Short human-readable product ID (task-103). Server auto-generates it on
  // create when omitted; sending it overrides (422 on blank/,/| or collision).
  product_code?: string | null;
}

export type BubbleTeaMenuItemInput = Omit<BubbleTeaMenuItemAdmin, 'menu_item_id'>;

// Per-file outcome of a bulk image upload, keyed by the uploaded filename.
// Mirrors backend `BulkImageUploadResult` in app/api/admin_bubbletea_options.py.
export interface BulkImageUploadResult {
  filename: string;
  success: boolean;
  menu_item_id: number | null;
  image_url: string | null;
  reason: string | null;
}

// filename -> menu_item_id mapping sent as a JSON-encoded form field.
export type BulkImageUploadMapping = Record<string, number>;

// Per-product option availability (task-028 Sprint 4, backing task-028-s3-1's
// GET/PATCH .../menu-items/{id}/options[/{option_item_id}/toggle] endpoints).
// Mirrors backend BubbleTeaMenuItemOptionToggleResponse in
// admin_bubbletea_options.py: `available` is the post-toggle state.
export interface MenuItemOptionToggleResponse {
  available: boolean;
}

// Mirrors backend BulkApplyOptionRequest/BulkApplyOptionResponse in
// admin_bubbletea_options.py (task-028-s3-2's POST .../menu-items/bulk-apply-option).
// menu_category is matched case-sensitively with no trimming/normalization on
// the backend, consistent with every other menu_category read/write.
export interface BulkApplyOptionRequest {
  option_item_id: number;
  menu_category: string;
  available: boolean;
}

export interface BulkApplyOptionResponse {
  // Count of products whose availability state actually changed -- not the
  // total number of products matched by menu_category.
  affected_count: number;
}

// Partial input shapes used for create/update calls
export type OptionCategoryInput = Omit<BubbleTeaOptionCategory, 'id' | 'items'> & { items?: never };
// combo_qr_unavailable is optional on create/update (backend: default false on
// create; omitted/null => unchanged on update).
export type OptionItemInput = Omit<BubbleTeaOptionItem, 'id' | 'category_id' | 'combo_qr_unavailable'> & {
  combo_qr_unavailable?: boolean;
};
export type InstantCakeInput = Omit<InstantCakeItem, 'id'>;

// ─── Option Categories ────────────────────────────────────────────────────────

export const adminBubbleTeaOptionCategories = {
  getAll: async (): Promise<BubbleTeaOptionCategory[]> => {
    const response = await axiosInstance.get<BubbleTeaOptionCategory[]>(`${BASE}/categories`);
    return response.data;
  },

  create: async (data: Partial<OptionCategoryInput>): Promise<BubbleTeaOptionCategory> => {
    const response = await axiosInstance.post<BubbleTeaOptionCategory>(`${BASE}/categories`, data);
    return response.data;
  },

  update: async (id: number, data: Partial<OptionCategoryInput>): Promise<BubbleTeaOptionCategory> => {
    const response = await axiosInstance.put<BubbleTeaOptionCategory>(`${BASE}/categories/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE}/categories/${id}`);
  },
};

// ─── Option Items ─────────────────────────────────────────────────────────────

export const adminBubbleTeaOptionItems = {
  create: async (categoryId: number, data: Partial<OptionItemInput>): Promise<BubbleTeaOptionItem> => {
    const response = await axiosInstance.post<BubbleTeaOptionItem>(
      `${BASE}/categories/${categoryId}/items`,
      data
    );
    return response.data;
  },

  update: async (id: number, data: Partial<OptionItemInput>): Promise<BubbleTeaOptionItem> => {
    const response = await axiosInstance.put<BubbleTeaOptionItem>(`${BASE}/items/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE}/items/${id}`);
  },

  toggle: async (id: number): Promise<BubbleTeaOptionItem> => {
    const response = await axiosInstance.patch<BubbleTeaOptionItem>(`${BASE}/items/${id}/toggle`);
    return response.data;
  },
};

// ─── Instant Cakes ────────────────────────────────────────────────────────────

export const adminInstantCakes = {
  getAll: async (): Promise<InstantCakeItem[]> => {
    const response = await axiosInstance.get<InstantCakeItem[]>(`${BASE}/instant-cakes`);
    return response.data;
  },

  create: async (data: Partial<InstantCakeInput>): Promise<InstantCakeItem> => {
    const response = await axiosInstance.post<InstantCakeItem>(`${BASE}/instant-cakes`, data);
    return response.data;
  },

  update: async (id: number, data: Partial<InstantCakeInput>): Promise<InstantCakeItem> => {
    const response = await axiosInstance.put<InstantCakeItem>(`${BASE}/instant-cakes/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE}/instant-cakes/${id}`);
  },

  updateStock: async (id: number, stockCount: number): Promise<InstantCakeItem> => {
    const response = await axiosInstance.patch<InstantCakeItem>(
      `${BASE}/instant-cakes/${id}/stock`,
      { stock_count: stockCount }
    );
    return response.data;
  },

  // Rotates the instant cake's qr_token to a new value, invalidating any
  // previously printed/saved QR code. 400s if qr_enabled is false.
  refreshQrCode: async (id: number): Promise<InstantCakeItem> => {
    const response = await axiosInstance.post<InstantCakeItem>(`${BASE}/instant-cakes/${id}/refresh-qr-code`);
    return response.data;
  },

  // GET /admin/bubble-tea-options/instant-cakes/{id}/qr-image is admin-gated
  // (Bearer token header, same as GET /admin/products/{id}/qr-image and the
  // menu-items equivalent above), so it can't be used as a plain <img src> --
  // fetch it through the authenticated axios client as a blob and let the
  // caller build an object URL from it. See task-020-s4-1's AdminProducts.tsx/
  // admin.ts for the originating pattern.
  getQrImage: async (id: number): Promise<Blob> => {
    const response = await axiosInstance.get(`${BASE}/instant-cakes/${id}/qr-image`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

// ─── Standalone function exports (matches task spec naming) ───────────────────

export const fetchBubbleTeaOptionCategories = async (): Promise<BubbleTeaOptionCategory[]> => {
  const response = await axiosInstance.get<BubbleTeaOptionCategory[]>('/admin/bubble-tea-options/categories');
  return response.data;
};
export const createOptionCategory = adminBubbleTeaOptionCategories.create;
export const updateOptionCategory = adminBubbleTeaOptionCategories.update;
export const deleteOptionCategory = adminBubbleTeaOptionCategories.delete;

export const createOptionItem = adminBubbleTeaOptionItems.create;
export const updateOptionItem = adminBubbleTeaOptionItems.update;
export const deleteOptionItem = adminBubbleTeaOptionItems.delete;
export const toggleOptionItem = adminBubbleTeaOptionItems.toggle;

// ─── Order Item Combo QR (task-032-impl-7) ─────────────────────────────────────

// GET /admin/quick-bites/orders/{order_id}/items/{item_id}/qr-image is
// admin-gated (Bearer token header, same pattern as adminInstantCakes.getQrImage/
// adminBubbleTeaMenuItems.getQrImage above), so it can't be used as a plain
// <img src> -- fetch it through the authenticated axios client as a blob and
// let the caller build an object URL from it. This is a pure lookup against
// eagerly-generated combo rows (see backend/app/api/admin.py::
// get_order_item_combo_qr_image, task-032-impl-4/7): a resolvable combo
// returns 200 with an image/png blob; a line item whose options don't
// resolve to any triggering combo is an expected, non-error "no QR needed"
// state and also returns 200, but with an application/json blob whose
// parsed body is {"has_qr": false} -- callers must sniff blob.type to tell
// the two 200 cases apart. A 404 now means a genuine failure (invalid
// order/item pair, or an admin-auth error) and should be treated as a real
// error, not folded into the "no QR needed" state. responseType stays
// 'blob' so both the PNG and JSON success bodies come back uniformly;
// callers that need the JSON payload can re-parse the blob's text.
// Deliberately NOT namespaced under `${BASE}` (`/admin/bubble-tea-options`)
// since this endpoint lives under `/admin/quick-bites/orders`, not the
// option-catalogue routes above.
export const getOrderItemQrImage = async (orderId: number, itemId: number): Promise<Blob> => {
  const response = await axiosInstance.get(
    `/admin/quick-bites/orders/${orderId}/items/${itemId}/qr-image`,
    { responseType: 'blob' }
  );
  return response.data as Blob;
};

export interface MenuItemQrComboRefreshResult {
  menu_item_id: number;
  combos: { combo_key: string; qr_token: string }[];
}

// POST /admin/quick-bites/menu-items/{menu_item_id}/qr-combos/refresh rotates
// every existing BubbleTeaMenuItemQrCombo.qr_token for this menu item to a
// fresh uuid4, then mints any newly-reachable combos (see
// backend/app/api/admin.py::refresh_qb_menu_item_qr_combos, task-032-impl-5).
// Scoped server-side to a single menu_item_id -- no other menu item's combo
// rows are touched. This is a manual, admin-triggered rotation of the
// drink-machine combo tokens (task-032) and is entirely unrelated to
// adminBubbleTeaMenuItems.refreshQrCode above, which rotates the single
// customer-facing qr_enabled/qr_token field from task-020 -- neither function
// calls the other, and this one never reads/writes that field. Deliberately
// NOT namespaced under `${MENU_BASE}` (`/admin/bubble-tea-options/menu-items`)
// since this endpoint lives under `/admin/quick-bites/menu-items`, matching
// getOrderItemQrImage's direct-path convention above.
export const refreshMenuItemQrCombos = async (menuItemId: number): Promise<MenuItemQrComboRefreshResult> => {
  const response = await axiosInstance.post<MenuItemQrComboRefreshResult>(
    `/admin/quick-bites/menu-items/${menuItemId}/qr-combos/refresh`
  );
  return response.data;
};

// Mirrors backend GET /admin/quick-bites/menu-items/{menu_item_id}/qr-combos
// (task-037-impl-1, backend/app/api/admin.py). `qr_image` is a full
// `data:image/png;base64,...` data URI, not a bare token/base64 string --
// there is no separate qr_token field on this response (unlike
// MenuItemQrComboRefreshResult above). Combos are pre-sorted by combo_key
// server-side.
export interface MenuItemQrComboListResult {
  menu_item_id: number;
  combos: { combo_key: string; qr_image: string }[];
}

// Purely read-only lookup -- never mutates/rotates qr_token, unlike
// refreshMenuItemQrCombos above. Deliberately NOT namespaced under
// `${MENU_BASE}` (`/admin/bubble-tea-options/menu-items`) since this
// endpoint lives under `/admin/quick-bites/menu-items`, matching
// refreshMenuItemQrCombos's direct-path convention above.
export const fetchMenuItemQrCombos = async (menuItemId: number): Promise<MenuItemQrComboListResult> => {
  const response = await axiosInstance.get<MenuItemQrComboListResult>(
    `/admin/quick-bites/menu-items/${menuItemId}/qr-combos`
  );
  return response.data;
};

export const fetchInstantCakes = adminInstantCakes.getAll;
export const createInstantCake = adminInstantCakes.create;
export const updateInstantCake = adminInstantCakes.update;
export const deleteInstantCake = adminInstantCakes.delete;
export const updateInstantCakeStock = adminInstantCakes.updateStock;
export const refreshInstantCakeQrCode = adminInstantCakes.refreshQrCode;
export const getInstantCakeQrImage = adminInstantCakes.getQrImage;

// ─── BubbleTeaMenuItems ───────────────────────────────────────────────────────

const MENU_BASE = '/admin/bubble-tea-options/menu-items';

export const adminBubbleTeaMenuItems = {
  getAll: async (): Promise<BubbleTeaMenuItemAdmin[]> => {
    const response = await axiosInstance.get<BubbleTeaMenuItemAdmin[]>(MENU_BASE);
    return response.data;
  },

  create: async (data: Partial<BubbleTeaMenuItemInput>): Promise<BubbleTeaMenuItemAdmin> => {
    const response = await axiosInstance.post<BubbleTeaMenuItemAdmin>(MENU_BASE, data);
    return response.data;
  },

  update: async (id: number, data: Partial<BubbleTeaMenuItemInput>): Promise<BubbleTeaMenuItemAdmin> => {
    const response = await axiosInstance.put<BubbleTeaMenuItemAdmin>(`${MENU_BASE}/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${MENU_BASE}/${id}`);
  },

  uploadImage: async (id: number, file: File): Promise<BubbleTeaMenuItemAdmin> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post<BubbleTeaMenuItemAdmin>(
      `${MENU_BASE}/${id}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  bulkUploadImages: async (
    files: File[],
    mapping: BulkImageUploadMapping
  ): Promise<BulkImageUploadResult[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('mapping', JSON.stringify(mapping));

    // task-058 fix_review: the backend processes every uploaded file
    // SEQUENTIALLY in one request/response cycle (S3-first-with-local-fallback
    // per file, each its own db.commit()), so cost scales linearly with file
    // count -- a dozen-plus-photo admin session on a slow connection can
    // plausibly exceed axios.ts's flat DEFAULT_REQUEST_TIMEOUT_MS (30000ms).
    // This is a deliberate, scoped per-call override for this one high-volume
    // operation -- task-058's "no per-request overrides" constraint targeted
    // keeping axios.ts's own ordinary-request timeout change minimal/
    // consistent, not a blanket ban on a justified exception here. Do NOT
    // apply this pattern to ordinary requests; do NOT touch axios.ts.
    const PER_FILE_TIMEOUT_ALLOWANCE_MS = 15000;
    const MIN_BULK_UPLOAD_TIMEOUT_MS = 30000;
    const MAX_BULK_UPLOAD_TIMEOUT_MS = 600000; // 10 minutes
    const timeout = Math.min(
      MAX_BULK_UPLOAD_TIMEOUT_MS,
      Math.max(MIN_BULK_UPLOAD_TIMEOUT_MS, files.length * PER_FILE_TIMEOUT_ALLOWANCE_MS)
    );

    const response = await axiosInstance.post<{ results: BulkImageUploadResult[] }>(
      `${MENU_BASE}/bulk-upload-images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout,
      }
    );
    return response.data.results;
  },

  // Rotates the menu item's qr_token to a new value, invalidating any
  // previously printed/saved QR code. 400s if qr_enabled is false.
  refreshQrCode: async (id: number): Promise<BubbleTeaMenuItemAdmin> => {
    const response = await axiosInstance.post<BubbleTeaMenuItemAdmin>(`${MENU_BASE}/${id}/refresh-qr-code`);
    return response.data;
  },

  // GET /admin/bubble-tea-options/menu-items/{id}/qr-image is admin-gated
  // (Bearer token header, same as GET /admin/products/{id}/qr-image), so it
  // can't be used as a plain <img src> -- fetch it through the authenticated
  // axios client as a blob and let the caller build an object URL from it.
  // See task-020-s4-1's AdminProducts.tsx/admin.ts for the originating pattern.
  getQrImage: async (id: number): Promise<Blob> => {
    const response = await axiosInstance.get(`${MENU_BASE}/${id}/qr-image`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // Per-product option availability (task-028 Sprint 4). Row presence in
  // bubble_tea_menu_item_options = available; row absence = unavailable --
  // there is no boolean flag column. Backed by task-028-s3-1's endpoints.
  getOptions: async (menuItemId: number): Promise<number[]> => {
    const response = await axiosInstance.get<number[]>(`${MENU_BASE}/${menuItemId}/options`);
    return response.data;
  },

  // Flips a single product's availability for a single option item: enabled
  // if it was disabled, and vice versa. Returns the post-toggle state.
  toggleOption: async (
    menuItemId: number,
    optionItemId: number
  ): Promise<MenuItemOptionToggleResponse> => {
    const response = await axiosInstance.patch<MenuItemOptionToggleResponse>(
      `${MENU_BASE}/${menuItemId}/options/${optionItemId}/toggle`
    );
    return response.data;
  },

  // Enables/disables one option item across every product whose menu_category
  // exactly matches request.menu_category (task-028-s3-2). Scope is
  // menu_category (the product grouping from AdminQBCategories.tsx) only --
  // never BubbleTeaOptionCategory. Returns the count of products that actually
  // changed state.
  bulkApplyOption: async (data: BulkApplyOptionRequest): Promise<BulkApplyOptionResponse> => {
    const response = await axiosInstance.post<BulkApplyOptionResponse>(
      `${MENU_BASE}/bulk-apply-option`,
      data
    );
    return response.data;
  },
};

export const fetchBubbleTeaMenuItems = adminBubbleTeaMenuItems.getAll;
export const createBubbleTeaMenuItem = adminBubbleTeaMenuItems.create;
export const updateBubbleTeaMenuItem = adminBubbleTeaMenuItems.update;
export const deleteBubbleTeaMenuItem = adminBubbleTeaMenuItems.delete;
export const uploadBubbleTeaMenuItemImage = adminBubbleTeaMenuItems.uploadImage;

// Aliases matching the sprint-3 task spec's exact function names, for the
// upcoming sprint-4 admin UI to import directly.
export const uploadMenuItemImage = adminBubbleTeaMenuItems.uploadImage;
export const bulkUploadMenuItemImages = adminBubbleTeaMenuItems.bulkUploadImages;
export const getMenuItemQrImage = adminBubbleTeaMenuItems.getQrImage;
export const refreshMenuItemQrCode = adminBubbleTeaMenuItems.refreshQrCode;

// Aliases matching the sprint-4 task spec's exact function names (task-028-s4-3),
// for the upcoming per-product/bulk-apply admin UI to import directly.
export const getMenuItemOptions = adminBubbleTeaMenuItems.getOptions;
export const toggleMenuItemOption = adminBubbleTeaMenuItems.toggleOption;
export const bulkApplyOptionToCategory = adminBubbleTeaMenuItems.bulkApplyOption;
